import type { Map as MapboxMap } from "mapbox-gl";

import type { PlaceLocation } from "api/map";

const PADDING = 40;
const MIN_ZOOM = 2;
const MAX_ZOOM = 16;
// The precision a place is recorded and shown at, wherever it is written. Exported so a
// second copy cannot drift from this one.
export const COORDINATE_DECIMALS = 6;
export const ZOOM_DECIMALS = 2;

// A place wider than half the world is an antimeridian artefact, not a real extent:
// the Aleutians reach both -179 and 179, so the bounding box spans everything between.
const MAX_TRUSTED_SPAN = 180;

// The map is given degrees. A place that arrives in projected metres is finite and looks
// usable, so only its range gives it away.
const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

// Used when the bounding box cannot be trusted, so the jump still lands on the place.
export const FALLBACK_ZOOM = {
  county: 9,
  township: 12,
  section: 14
};

export interface JumpCamera {
  lng: number;
  lat: number;
  zoom: number;
}

function isUsableNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isUsableLongitude(value: unknown): value is number {
  return isUsableNumber(value) && Math.abs(value) <= MAX_LONGITUDE;
}

function isUsableLatitude(value: unknown): value is number {
  return isUsableNumber(value) && Math.abs(value) <= MAX_LATITUDE;
}

/**
 * Rounds through `toFixed`, deliberately: it makes `value === Number(value.toFixed(n))`
 * true of the result, so a number recorded here is already the number a `.toFixed(n)`
 * display will show. `roundTo` in utils/numbers multiplies instead and does not
 * guarantee that.
 */
export function round(value: number, decimals: number): number {
  return Number(value.toFixed(decimals));
}

function toCamera(lng: number, lat: number, zoom: number): JumpCamera {
  return {
    lng: round(lng, COORDINATE_DECIMALS),
    lat: round(lat, COORDINATE_DECIMALS),
    zoom: round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom)), ZOOM_DECIMALS)
  };
}

// The centroid is what every fallback aims at, so a place without one has nothing to aim
// at. Nothing comes back rather than a rounded null, which the map throws on.
function centroidCamera(place: PlaceLocation, zoom: number): Nullable<JumpCamera> {
  const { centroidLng, centroidLat } = place;
  if (!isUsableLongitude(centroidLng) || !isUsableLatitude(centroidLat)) {
    return null;
  }
  return toCamera(centroidLng, centroidLat, zoom);
}

/**
 * Works out where the map should sit to show a place. It returns numbers only — the
 * caller applies them — and it rounds them, so the same place always gives the same
 * camera and the camera that gets applied is the camera that gets recorded. A place with
 * neither a bounding box that can be fitted nor a usable centroid gives nothing back.
 */
export function resolveJumpCamera(
  mapbox: MapboxMap,
  place: PlaceLocation,
  fallbackZoom: number
): Nullable<JumpCamera> {
  const { minx, miny, maxx, maxy } = place;

  if (maxx - minx > MAX_TRUSTED_SPAN) {
    return centroidCamera(place, fallbackZoom);
  }

  // A box with no area fits at maxZoom rather than failing, which is the opposite of
  // what a point-sized place needs.
  if (minx === maxx && miny === maxy) {
    return centroidCamera(place, fallbackZoom);
  }

  const camera = mapbox.cameraForBounds(
    [
      [minx, miny],
      [maxx, maxy]
    ],
    { padding: PADDING, maxZoom: MAX_ZOOM, bearing: 0, pitch: 0 }
  );
  const center = camera?.center as { lng: number; lat: number } | undefined;
  const zoom = camera?.zoom;

  // A map in a container with no size fits nothing and answers with NaN, which would
  // reach the map and throw — the user would see "Unable to find location" for a place
  // the service resolved perfectly well. The centroid is what saves that case, and it is
  // also what a fit of coordinates that are not degrees falls back to.
  if (
    !center ||
    !isUsableLongitude(center.lng) ||
    !isUsableLatitude(center.lat) ||
    !isUsableNumber(zoom)
  ) {
    return centroidCamera(place, fallbackZoom);
  }

  return toCamera(center.lng, center.lat, zoom);
}
